import { useState, useRef, useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Camera, Grid3X3, Loader2, Check, X, Sparkles, AlertCircle, Plus, ImagePlus, ArrowRight,
} from 'lucide-react';
import confetti from 'canvas-confetti';
import { flagFor } from '../lib/flags';
import { useAuth } from '../hooks/useAuth';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';

const MAX_DIMENSION = 1280;
const MAX_UPLOAD_BYTES = 4 * 1024 * 1024;
const MAX_BATCH = 10;

async function prepareForUpload(file: File): Promise<File> {
  if (!file.type.startsWith('image/') && !/\.heic$/i.test(file.name)) return file;
  let working: File = file;
  if (file.type === 'image/heic' || /\.heic$/i.test(file.name)) {
    try {
      const heic2any = (await import('heic2any')).default;
      const blob = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 });
      const jpegBlob = Array.isArray(blob) ? blob[0] : blob;
      working = new File(
        [jpegBlob],
        file.name.replace(/\.heic$/i, '.jpg'),
        { type: 'image/jpeg', lastModified: Date.now() },
      );
    } catch {
      return file;
    }
  }
  const bitmap = await createImageBitmap(working).catch(() => null);
  if (!bitmap) return working;
  const { width, height } = bitmap;
  const longest = Math.max(width, height);
  const scale = longest > MAX_DIMENSION ? MAX_DIMENSION / longest : 1;
  if (scale === 1 && working.size <= 400 * 1024) {
    bitmap.close?.();
    return working;
  }
  const w = Math.round(width * scale);
  const h = Math.round(height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) { bitmap.close?.(); return working; }
  ctx.drawImage(bitmap, 0, 0, w, h);
  bitmap.close?.();
  const blob: Blob | null = await new Promise((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', 0.82),
  );
  if (!blob) return working;
  return new File([blob], working.name.replace(/\.[^.]+$/, '') + '.jpg', {
    type: 'image/jpeg', lastModified: Date.now(),
  });
}

type Step = 'choose' | 'gallery' | 'analyzing' | 'review' | 'done';

type PhotoStatus = 'ready' | 'queued' | 'analyzing' | 'done' | 'error';
type Photo = {
  id: string;
  file: File;
  previewUrl: string;
  status: PhotoStatus;
  result?: {
    uploadId: string;
    pageNumber: number | null;
    printedPageNumber?: number | null;
    pageTitle?: string | null;
    presentNumbers: number[];
    missingNumbers?: number[];
    isHalfPage?: boolean;
    confidence: number;
  };
  error?: string;
};

export function Upload() {
  const { token } = useAuth();
  const qc = useQueryClient();
  const nav = useNavigate();
  const { t } = useI18n();
  const inputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>('choose');
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [pickError, setPickError] = useState<string | null>(null);
  const [optimizing, setOptimizing] = useState(false);
  const [optimizeProgress, setOptimizeProgress] = useState<{ current: number; total: number } | null>(null);
  const [reviewIndex, setReviewIndex] = useState(0);
  const [excluded, setExcluded] = useState<Set<number>>(new Set());
  // Cuando la usuaria toca una card "Falta" para marcarla como Tengo (override del AI)
  const [promoted, setPromoted] = useState<Set<number>>(new Set());

  // Catálogo para mostrar nombre del país + jugador en cada slot detectado
  const { data: col } = useQuery({
    queryKey: ['collection'],
    queryFn: () => api.collection(token!),
    enabled: !!token,
    staleTime: 5 * 60_000,
  });

  const stickerByNumber = useMemo(() => {
    const m = new Map<number, any>();
    for (const s of col?.stickers ?? []) m.set(s.number, s);
    return m;
  }, [col]);

  function localCode(n: number): string {
    const s = stickerByNumber.get(n);
    if (!s) return `#${n}`;
    if (s.code) return s.code.replace(/-/g, ' ');
    return `#${n}`;
  }
  function playerOf(n: number): string | null {
    const s = stickerByNumber.get(n);
    return s?.player_name ?? null;
  }

  const currentPhoto = photos[reviewIndex];
  const currentResult = currentPhoto?.result;
  const detectedPageTitle = useMemo(() => {
    if (!currentResult) return null;
    if (currentResult.pageTitle) return currentResult.pageTitle;
    for (const n of currentResult.presentNumbers) {
      const s = stickerByNumber.get(n);
      if (s?.page_title) return s.page_title as string;
    }
    return null;
  }, [currentResult, stickerByNumber]);

  function chooseFile() { setPickError(null); inputRef.current?.click(); }

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (files.length === 0) return;

    const remaining = MAX_BATCH - photos.length;
    if (remaining <= 0) {
      setPickError(`Ya cargaste ${MAX_BATCH} fotos (el tope). Analizalas o quitá alguna.`);
      return;
    }
    const toProcess = files.slice(0, remaining);
    const skipped = files.length - toProcess.length;

    setPickError(null);
    setOptimizing(true);
    setOptimizeProgress({ current: 0, total: toProcess.length });
    try {
      const prepared: Photo[] = [];
      let i = 0;
      for (const raw of toProcess) {
        i++;
        setOptimizeProgress({ current: i, total: toProcess.length });
        const optimized = await prepareForUpload(raw);
        if (optimized.size > MAX_UPLOAD_BYTES) continue;
        prepared.push({
          id: crypto.randomUUID(),
          file: optimized,
          previewUrl: URL.createObjectURL(optimized),
          status: 'ready',
        });
      }
      setPhotos((p) => [...p, ...prepared]);
      setStep('gallery');
      if (skipped > 0) {
        setPickError(`Se cargaron ${prepared.length}. ${skipped} foto${skipped === 1 ? '' : 's'} no entraron por el tope de ${MAX_BATCH}.`);
      }
    } catch {
      setPickError('No se pudieron leer las fotos.');
    } finally {
      setOptimizing(false);
      setOptimizeProgress(null);
    }
  }

  function removePhoto(id: string) {
    setPhotos((p) => p.filter((x) => x.id !== id));
  }

  async function startAnalysisBatch() {
    if (!token || photos.length === 0) return;
    setStep('analyzing');
    setPhotos((p) => p.map((x) => ({ ...x, status: 'queued' })));

    // Procesa en paralelo con concurrencia 2 (evita freezes + buena velocidad)
    const concurrency = 2;
    const queue = [...photos];
    let active = 0;
    let idx = 0;
    await new Promise<void>((resolve) => {
      function next() {
        while (active < concurrency && idx < queue.length) {
          const photo = queue[idx];
          idx++;
          active++;
          processOne(photo).finally(() => {
            active--;
            if (idx >= queue.length && active === 0) resolve();
            else next();
          });
        }
      }
      next();
    });

    // Arrancar la revisión sobre la primera foto procesada con éxito
    setExcluded(new Set());
    setPromoted(new Set());
    setPhotos((current) => {
      const firstDone = current.findIndex((p) => p.status === 'done');
      if (firstDone === -1) {
        // Ninguna foto válida: volver a la galería para revisar errores
        setStep('gallery');
      } else {
        setReviewIndex(firstDone);
        setStep('review');
      }
      return current;
    });
  }

  async function processOne(photo: Photo) {
    setPhotos((p) => p.map((x) => (x.id === photo.id ? { ...x, status: 'analyzing' } : x)));
    try {
      const up = await api.upload(photo.file, token!);
      const an = await api.analyze(up.uploadId, token!);
      setPhotos((p) =>
        p.map((x) => (x.id === photo.id ? { ...x, status: 'done', result: an as any } : x)),
      );
    } catch (e: any) {
      setPhotos((p) =>
        p.map((x) => (x.id === photo.id ? { ...x, status: 'error', error: e?.message ?? 'error' } : x)),
      );
    }
  }

  function toggleNumber(n: number) {
    setExcluded((s) => {
      const next = new Set(s);
      next.has(n) ? next.delete(n) : next.add(n);
      return next;
    });
  }

  const confirmMut = useMutation({
    mutationFn: async () => {
      if (!currentResult) return;
      // Tengo finales = present - excluded + promoted (los que la usuaria marcó manualmente)
      const additions = [
        ...currentResult.presentNumbers.filter((n) => !excluded.has(n)),
        ...promoted,
      ];
      const removals = [...excluded];
      return api.confirmVision(currentResult.uploadId, additions, removals, token!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['collection'] });
      qc.invalidateQueries({ queryKey: ['missing'] });
      advanceReview();
    },
  });

  function advanceReview() {
    let nextIdx = reviewIndex + 1;
    // Saltar fotos con error o sin result válido
    while (nextIdx < photos.length && photos[nextIdx].status !== 'done') {
      nextIdx++;
    }
    if (nextIdx >= photos.length) {
      // Todos revisados → confetti + done
      confetti({
        particleCount: 150, spread: 80, origin: { y: 0.4 },
        colors: ['#ff3d6b', '#ffd166', '#c6ff5e', '#5ee0ff'],
      });
      setStep('done');
      setTimeout(() => nav('/app'), 1800);
      return;
    }
    setReviewIndex(nextIdx);
    setExcluded(new Set());
  }

  function skipCurrent() { advanceReview(); }
  function startOver() {
    photos.forEach((p) => URL.revokeObjectURL(p.previewUrl));
    setPhotos([]);
    setReviewIndex(0);
    setExcluded(new Set());
    setStep('choose');
  }

  // Limpiar URLs al desmontar
  useEffect(() => () => photos.forEach((p) => URL.revokeObjectURL(p.previewUrl)), []); // eslint-disable-line

  // Wake lock + warn beforeunload durante análisis: que el cel no se duerma y avisar si intenta salir
  useEffect(() => {
    if (step !== 'analyzing' && !optimizing) return;

    let wakeLock: any = null;
    const wakeLockSentinel = async () => {
      try {
        // @ts-ignore — wakeLock no está siempre en TS lib
        if (navigator.wakeLock) {
          // @ts-ignore
          wakeLock = await navigator.wakeLock.request('screen');
        }
      } catch { /* noop */ }
    };
    wakeLockSentinel();

    function onBeforeUnload(e: BeforeUnloadEvent) {
      e.preventDefault();
      e.returnValue = '';
    }
    window.addEventListener('beforeunload', onBeforeUnload);

    // Si la pestaña vuelve a foreground después de estar oculta, intentar re-adquirir wake lock
    function onVisibility() {
      if (document.visibilityState === 'visible' && !wakeLock) wakeLockSentinel();
    }
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('visibilitychange', onVisibility);
      if (wakeLock && wakeLock.release) wakeLock.release().catch(() => {});
    };
  }, [step, optimizing]);

  // Scroll al top cuando cambia de paso o de país en el review
  // (sin esto, queda mostrando la mitad de la pantalla anterior y parece colgada)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [reviewIndex, step]);

  const remaining = MAX_BATCH - photos.length;
  const atLimit = remaining <= 0;
  const doneCount = photos.filter((p) => p.status === 'done').length;
  const errorCount = photos.filter((p) => p.status === 'error').length;

  return (
    <div className="space-y-5">
      {/* Overlay durante optimización (iOS HEIC → JPEG es lento) */}
      <AnimatePresence>
        {optimizing && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-ink/85 backdrop-blur-sm grid place-items-center px-6"
          >
            <div className="glass-strong rounded-3xl p-8 max-w-sm w-full text-center">
              <Loader2 size={36} className="mx-auto animate-spin text-flame" />
              <div className="mt-4 font-display font-bold text-xl">{t('upload.preparing')}</div>
              {optimizeProgress && (
                <div className="mt-3 font-display font-extrabold text-3xl text-gradient tabular-nums">
                  {optimizeProgress.current} / {optimizeProgress.total}
                </div>
              )}
              <p className="mt-3 text-2xs text-haze leading-relaxed">{t('upload.preparingDesc')}</p>
              <p className="mt-2 text-2xs text-flame font-bold uppercase tracking-wider">
                {t('upload.warnStay')}
              </p>
              <div className="mt-5 h-1 rounded-full bg-white/8 overflow-hidden">
                <motion.div
                  className="h-full bg-flame"
                  animate={{ width: optimizeProgress ? `${(optimizeProgress.current / optimizeProgress.total) * 100}%` : '0%' }}
                  transition={{ duration: 0.3 }}
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <header>
        <p className="text-2xs uppercase tracking-widest text-haze">{t('upload.eyebrow')}</p>
        <h1 className="font-display font-extrabold text-3xl">{t('upload.title')}</h1>
      </header>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={onFile}
      />

      <AnimatePresence mode="wait">
        {step === 'choose' && (
          <motion.div key="choose" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-3">
            <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
              <OptionCard
                icon={<Camera size={28} />}
                tone="flame"
                title={t('upload.photoTitle')}
                body={t('upload.photoBody')}
                cta={optimizing ? t('upload.optimizing') : t('upload.chooseFiles')}
                onClick={chooseFile}
              />
              <OptionCard
                icon={<Grid3X3 size={28} />}
                tone="lime"
                title={t('upload.manualTitle')}
                body={t('upload.manualBody')}
                cta={t('upload.openGrid')}
                to="/app/manual"
              />
            </div>
            <Link
              to="/app/upload-loose"
              className="block relative overflow-hidden rounded-2xl glass-strong p-4 flex items-center gap-3 hover:bg-graphite/40 transition group"
            >
              <div className="h-11 w-11 rounded-xl grid place-items-center bg-cyan/15 text-cyan border border-cyan/40 group-hover:bg-cyan/25 transition shrink-0">
                <Sparkles size={20} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-display font-extrabold text-base leading-tight">{t('upload.looseTitle')}</div>
                <div className="text-2xs text-haze mt-0.5">
                  {t('upload.looseBody')}
                </div>
              </div>
              <div className="pointer-events-none absolute -top-8 -right-8 h-24 w-24 rounded-full bg-cyan/20 blur-2xl group-hover:bg-cyan/35 transition" />
            </Link>

            <div className="glass rounded-2xl p-4 space-y-2">
              <div className="flex items-center gap-2 text-cyan font-semibold text-sm">
                <Sparkles size={14} /> {t('upload.tips.title')}
              </div>
              <ul className="text-2xs text-haze space-y-1 ml-1">
                <li>• {t('upload.tips.t1')}</li>
                <li>• {t('upload.tips.t2')}</li>
                <li>• {t('upload.tips.t3')}</li>
                <li>• {t('upload.tips.t4')}</li>
                <li>• {t('upload.tips.t5')}</li>
              </ul>
            </div>

            {pickError && (
              <div className="flex items-start gap-2 rounded-xl border border-flame/40 bg-flame/10 p-3 text-sm">
                <AlertCircle size={18} className="mt-0.5 shrink-0 text-flame" />
                <div className="text-flame">{pickError}</div>
              </div>
            )}
          </motion.div>
        )}

        {(step === 'gallery' || step === 'analyzing') && (
          <motion.div key="gal" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
            {/* Encabezado de la galería */}
            <div className="glass-strong rounded-2xl p-4 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-2xs uppercase tracking-widest text-haze">
                  {step === 'analyzing' ? t('upload.gallery.analyzing') : t('upload.gallery.eyebrow')}
                </div>
                <div className="font-display font-extrabold text-xl">
                  {photos.length} {t('common.from')} {MAX_BATCH} {t('upload.gallery.count')}
                </div>
                {step === 'analyzing' && (
                  <div className="text-2xs text-haze mt-0.5">
                    {t('upload.gallery.done')}: <span className="text-lime font-semibold">{doneCount}</span>
                    {errorCount > 0 && <> · {t('upload.gallery.failed')}: <span className="text-flame font-semibold">{errorCount}</span></>}
                  </div>
                )}
              </div>
              {atLimit && step === 'gallery' && (
                <span className="chip-flame text-2xs">{t('upload.gallery.limitReached')}</span>
              )}
            </div>

            {/* Grid de thumbnails */}
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
              {photos.map((p) => (
                <PhotoTile
                  key={p.id}
                  photo={p}
                  onRemove={step === 'gallery' ? () => removePhoto(p.id) : undefined}
                />
              ))}
              {/* Slots vacíos hasta MAX_BATCH */}
              {step === 'gallery' && !atLimit && Array.from({ length: remaining }).map((_, i) => (
                <button
                  key={`empty-${i}`}
                  onClick={i === 0 ? chooseFile : undefined}
                  disabled={i !== 0 || optimizing}
                  className={`aspect-square rounded-xl border-2 border-dashed ${
                    i === 0
                      ? 'border-flame/40 text-flame hover:bg-flame/5 cursor-pointer'
                      : 'border-line/30 text-haze/30'
                  } grid place-items-center transition`}
                >
                  {i === 0 ? <Plus size={22} strokeWidth={2.5} /> : <ImagePlus size={18} className="opacity-40" />}
                </button>
              ))}
            </div>

            {pickError && (
              <div className="flex items-start gap-2 rounded-xl border border-flame/40 bg-flame/10 p-3 text-sm">
                <AlertCircle size={18} className="mt-0.5 shrink-0 text-flame" />
                <div className="text-flame">{pickError}</div>
              </div>
            )}

            {/* Footer fijo de acciones */}
            {step === 'gallery' && (
              <div className="flex gap-2 sticky bottom-24">
                {!atLimit && (
                  <button className="btn-ghost flex-1" onClick={chooseFile} disabled={optimizing}>
                    <Plus size={14} /> {optimizing ? '…' : t('upload.gallery.uploadMore')}
                  </button>
                )}
                <button
                  className="btn-primary flex-1"
                  onClick={startAnalysisBatch}
                  disabled={photos.length === 0 || optimizing}
                >
                  <Sparkles size={16} /> {t('upload.gallery.analyze')} ({photos.length})
                </button>
              </div>
            )}

            {step === 'analyzing' && (
              <>
                <div className="rounded-2xl border-2 border-flame/50 bg-flame/15 p-4 flex items-start gap-3 shadow-glow-flame">
                  <AlertCircle size={20} className="shrink-0 mt-0.5 text-flame" />
                  <div>
                    <div className="font-display font-extrabold text-base text-flame leading-tight">
                      {t('upload.warnStay')}
                    </div>
                    <p className="text-2xs text-haze mt-1 leading-relaxed">
                      {t('upload.warnStayDesc')}
                    </p>
                  </div>
                </div>
                <div className="glass rounded-2xl p-4 text-center text-sm text-haze">
                  <Loader2 size={18} className="inline animate-spin text-flame mr-2" />
                  {t('upload.gallery.processing')} {doneCount + (photos.length - doneCount - errorCount)} / {photos.length}…
                </div>
              </>
            )}
          </motion.div>
        )}

        {step === 'review' && currentResult && currentPhoto && (
          <motion.div key="rev" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
            {/* Banner si hubo fotos que fallaron */}
            {errorCount > 0 && (
              <div className="rounded-2xl border border-flame/40 bg-flame/10 p-3 flex items-start gap-2 text-sm">
                <AlertCircle size={16} className="mt-0.5 shrink-0 text-flame" />
                <div className="text-flame">
                  <span className="font-bold">{errorCount}</span> {t('upload.review.failedBanner')}
                </div>
              </div>
            )}

            {/* Indicador foto X de Y */}
            <div className="rounded-2xl border border-cyan/30 bg-cyan/8 p-3 flex items-center justify-between gap-3">
              <div className="text-sm font-semibold text-cyan">
                {t('upload.review.reviewOf')} {reviewIndex + 1} {t('common.from')} {photos.length}
              </div>
              <div className="flex gap-1">
                {photos.map((p, i) => (
                  <span
                    key={p.id}
                    className={`h-2 w-2 rounded-full ${
                      i === reviewIndex ? 'bg-cyan' :
                      i < reviewIndex ? 'bg-lime' :
                      p.status === 'error' ? 'bg-flame/60' : 'bg-white/15'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Thumbnail de la foto actual */}
            <div className="glass-strong rounded-2xl overflow-hidden">
              <img src={currentPhoto.previewUrl} className="w-full max-h-44 object-cover" alt="foto" />
            </div>

            <div className="glass-strong rounded-3xl p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-2xs uppercase tracking-widest text-haze">{t('upload.review.eyebrow')}</p>
                  <h2 className="font-display font-extrabold text-2xl flex items-center gap-2 mt-0.5">
                    {flagFor(detectedPageTitle) && (
                      <span className="text-2xl leading-none">{flagFor(detectedPageTitle)}</span>
                    )}
                    {detectedPageTitle ?? t('upload.review.title')}
                  </h2>
                  <p className="mt-1 text-sm text-haze">
                    {currentResult.printedPageNumber ? `${t('upload.review.page')} ${currentResult.printedPageNumber} · ` : ''}
                    {currentResult.presentNumbers.length} {t('common.from')} 20 {t('upload.review.detectedCount')}
                  </p>
                </div>
                <span className="chip-cyan shrink-0">
                  {Math.round(currentResult.confidence * 100)}%
                </span>
              </div>
              <p className="mt-3 text-2xs text-haze">{t('upload.review.uncheck')}</p>
            </div>

            {/* Banner si es media página */}
            {currentResult.isHalfPage && (
              <div className="rounded-2xl border border-cyan/40 bg-cyan/10 p-4 flex items-start gap-3">
                <Sparkles size={18} className="text-cyan shrink-0 mt-0.5" />
                <div>
                  <div className="font-display font-extrabold text-sm text-cyan leading-tight">
                    {t('upload.review.halfPage')}
                  </div>
                  <p className="mt-1 text-2xs text-haze leading-relaxed">
                    {t('upload.review.halfPageDesc')}
                  </p>
                </div>
              </div>
            )}

            {/* Grid único ordenado por posición — verde tengo, rojo falta */}
            {(() => {
              const presentSet = new Set(currentResult.presentNumbers);
              const missingSet = new Set(currentResult.missingNumbers ?? []);
              const allNumbers = [
                ...currentResult.presentNumbers,
                ...(currentResult.missingNumbers ?? []),
              ];
              // Ordenar por posición (igual que en el álbum físico)
              const sorted = allNumbers
                .map((n) => ({ n, s: stickerByNumber.get(n) }))
                .filter((x) => !!x.s)
                .sort((a, b) => (a.s.position ?? a.s.number) - (b.s.position ?? b.s.number));

              return (
                <div className="space-y-2">
                  <div className="flex items-center gap-3 text-2xs uppercase tracking-widest font-bold ml-1">
                    <span className="text-lime flex items-center gap-1">
                      <Check size={12} /> {t('upload.review.have')} · {currentResult.presentNumbers.length}
                    </span>
                    {(currentResult.missingNumbers?.length ?? 0) > 0 && (
                      <span className="text-[#FF6B7A] flex items-center gap-1">
                        <X size={12} /> {t('upload.review.missing')} · {currentResult.missingNumbers!.length}
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {sorted.map(({ n, s }) => {
                      const isPresent = presentSet.has(n);
                      const isMissing = missingSet.has(n);
                      const isOut = excluded.has(n);
                      const isPromoted = promoted.has(n);
                      const code = localCode(n);
                      const player = playerOf(n);
                      // Estado visual final:
                      //  - present + no out → lime (tengo)
                      //  - missing + promoted → lime (override del usuario, "la tengo igual")
                      //  - present + out → gris (sacó del review)
                      //  - missing + no promoted → rojo (falta)
                      const showAsHave = (isPresent && !isOut) || (isMissing && isPromoted);
                      const styles = showAsHave
                        ? 'bg-lime/12 border-lime/45 shadow-[0_0_18px_-6px_rgba(198,255,94,0.5)]'
                        : isPresent && isOut
                          ? 'bg-void/40 border-line text-haze'
                          : 'bg-flame/10 border-flame/45 shadow-[0_0_18px_-6px_rgba(230,0,18,0.5)]';
                      const codeColor = showAsHave
                        ? 'text-lime'
                        : isPresent && isOut
                          ? 'text-haze line-through'
                          : 'text-[#FF6B7A]';
                      return (
                        <button
                          key={n}
                          onClick={() => {
                            if (isPresent) toggleNumber(n);
                            else if (isMissing) {
                              setPromoted((p) => {
                                const next = new Set(p);
                                next.has(n) ? next.delete(n) : next.add(n);
                                return next;
                              });
                            }
                          }}
                          className={`relative rounded-xl p-3 text-left transition border ${styles}`}
                        >
                          <div className="flex items-center justify-between gap-1">
                            <span className={`font-mono font-extrabold text-sm tabular-nums ${codeColor}`}>
                              {code}
                            </span>
                            {showAsHave && <Check size={14} className="text-lime shrink-0" />}
                            {isPresent && isOut && <X size={14} className="text-haze shrink-0" />}
                            {isMissing && !isPromoted && (
                              <span className="text-[9px] tracking-[0.18em] text-[#FF6B7A]/85 font-bold uppercase">
                                {t('upload.review.missing')}
                              </span>
                            )}
                          </div>
                          {player && (
                            <div className={`mt-1 text-2xs line-clamp-1 ${(isPresent && isOut) ? 'text-haze/60' : 'text-haze'}`}>
                              {player}
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {confirmMut.isError && (
              <div className="flex items-start gap-2 rounded-xl border border-flame/40 bg-flame/10 p-3 text-sm">
                <AlertCircle size={18} className="mt-0.5 shrink-0 text-flame" />
                <div className="text-flame">{(confirmMut.error as Error)?.message}</div>
              </div>
            )}

            <div className="flex gap-2 sticky bottom-24">
              <button className="btn-ghost flex-1" onClick={skipCurrent}>
                {t('common.skip')} <ArrowRight size={14} />
              </button>
              <button
                className="btn-primary flex-1"
                onClick={() => confirmMut.mutate()}
                disabled={confirmMut.isPending}
              >
                {confirmMut.isPending ? t('common.saving') : reviewIndex + 1 < photos.length ? t('upload.review.saveAndNext') : t('upload.review.saveAndFinish')}
              </button>
            </div>
          </motion.div>
        )}

        {step === 'done' && (
          <motion.div key="done" initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="glass-strong rounded-3xl p-10 text-center sweep-layer">
            <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-lime/20 text-lime animate-pulseglow">
              <Check size={32} />
            </div>
            <div className="mt-4 font-display font-extrabold text-2xl text-gradient">{t('upload.done.title')}</div>
            <div className="mt-1 text-sm text-haze">{t('upload.done.subtitle')}</div>
          </motion.div>
        )}
      </AnimatePresence>

      {step === 'review' && (
        <button onClick={startOver} className="text-2xs text-haze underline mx-auto block mt-2">
          {t('common.startOver')}
        </button>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// PhotoTile — thumbnail con estado
// ─────────────────────────────────────────────────────────────────────
function PhotoTile({ photo, onRemove }: { photo: Photo; onRemove?: () => void }) {
  const { t } = useI18n();
  const detectedTitle = photo.result?.pageTitle ?? null;
  const flag = flagFor(detectedTitle);
  function errorLabel(err?: string): string {
    if (!err) return t('common.error');
    if (err.includes('manual_only_section')) return t('upload.errorLabels.manual');
    if (err.includes('not_panini')) return t('upload.errorLabels.notPanini');
    if (err.includes('ai_blocked_abuse')) return t('upload.errorLabels.aiBlocked');
    return t('common.error');
  }
  return (
    <div className="relative aspect-square rounded-xl overflow-hidden border border-line/50 bg-ink/40 group">
      <img src={photo.previewUrl} className="absolute inset-0 h-full w-full object-cover" alt="thumb" />
      {/* Overlay según estado */}
      {photo.status === 'queued' && (
        <div className="absolute inset-0 bg-ink/60 grid place-items-center text-cyan text-2xs font-semibold">
          {t('upload.gallery.processing').toLowerCase()}…
        </div>
      )}
      {photo.status === 'analyzing' && (
        <div className="absolute inset-0 bg-ink/60 grid place-items-center">
          <Loader2 size={22} className="animate-spin text-flame" />
        </div>
      )}
      {photo.status === 'done' && (
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink/95 via-ink/70 to-transparent p-1.5">
          <div className="flex items-center gap-1 text-2xs font-bold text-lime">
            <Check size={11} />
            <span className="truncate">
              {flag && <span className="mr-0.5">{flag}</span>}
              {detectedTitle ?? t('upload.review.title')}
            </span>
          </div>
        </div>
      )}
      {photo.status === 'error' && (
        <div className="absolute inset-0 bg-flame/40 grid place-items-center text-center px-1">
          <div>
            <X size={20} className="text-flame mx-auto" />
            <div className="text-[9px] text-flame font-semibold mt-0.5 line-clamp-2">
              {errorLabel(photo.error)}
            </div>
          </div>
        </div>
      )}
      {onRemove && (
        <button
          onClick={onRemove}
          aria-label="quitar"
          className="absolute top-1 right-1 grid h-6 w-6 place-items-center rounded-full bg-ink/80 border border-line text-haze hover:text-flame hover:bg-ink transition"
        >
          <X size={12} />
        </button>
      )}
    </div>
  );
}

function OptionCard({ icon, tone, title, body, cta, onClick, to }: {
  icon: React.ReactNode; tone: 'flame' | 'lime';
  title: string; body: string; cta: string;
  onClick?: () => void; to?: string;
}) {
  const t = tone === 'flame'
    ? { border: 'border-flame/40', bg: 'bg-flame/10', text: 'text-flame', shadow: 'shadow-glow-flame', btn: 'btn-primary' }
    : { border: 'border-lime/40',  bg: 'bg-lime/10',  text: 'text-lime',  shadow: 'shadow-glow-lime',  btn: 'btn-lime' };
  const Body = (
    <div className={`relative overflow-hidden glass-strong rounded-3xl p-6 ${t.shadow}`}>
      <div className={`inline-grid h-14 w-14 place-items-center rounded-2xl border ${t.border} ${t.bg} ${t.text}`}>
        {icon}
      </div>
      <div className="mt-4 font-display font-extrabold text-xl">{title}</div>
      <div className="mt-1 text-sm text-haze leading-relaxed">{body}</div>
      <div className={`mt-5 ${t.btn} w-full`}>{cta}</div>
      <div className={`absolute -top-16 -right-16 h-40 w-40 rounded-full opacity-20 blur-2xl ${tone === 'flame' ? 'bg-flame' : 'bg-lime'}`} />
    </div>
  );
  if (to) return <Link to={to}>{Body}</Link>;
  return <button onClick={onClick} className="text-left">{Body}</button>;
}
